import { useEffect } from 'react';
import styled from '@emotion/styled';
import * as M from '@mantine/core';
import * as MDZ from '@mantine/dropzone';
import * as MN from '@mantine/notifications';
import { IconPhoto } from '@tabler/icons-react';

import { useUploadScheduleImageMutation } from 'features/scheduleConfiguration/hooks/mutations';
import { useUploadedScheduleImages } from 'features/scheduleConfiguration/hooks/queries';

import ImageGallery from 'features/components/ImageGallery';

interface Props {
  schoolId: string;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const ImageUploader = ({ schoolId, setIsLoading }: Props) => {
  const {
    mutate: uploadScheduleImage,
    isLoading: isUploading,
    error: uploadError,
  } = useUploadScheduleImageMutation(schoolId, {
    onError: (error) => {
      MN.showNotification({
        message:
          error instanceof Error
            ? error.message
            : 'Schedule detection failed. Please try again.',
        color: 'red',
      });
    },
  });

  const {
    data: images,
    isLoading: isParsing,
    error: imageLoadingError,
  } = useUploadedScheduleImages(schoolId);

  const isLoading = isUploading || (isParsing && !imageLoadingError);

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading, setIsLoading]);

  return (
    <Container>
      <MDZ.Dropzone
        onDrop={(files) => {
          if (files.length < 1) {
            return;
          }
          uploadScheduleImage(files[0]);
        }}
        accept={MDZ.IMAGE_MIME_TYPE}
        multiple={false}
        loading={isUploading}
      >
        <DropzoneContainer>
          <MDZ.Dropzone.Idle>
            <IconPhoto size={50} stroke={1.5} />
          </MDZ.Dropzone.Idle>

          <M.Text>Drag image here or click to select file</M.Text>
        </DropzoneContainer>
      </MDZ.Dropzone>

      {isLoading && <M.Loader sx={{ alignSelf: 'center' }} />}

      {isUploading && <M.Text role="status">Analyzing schedule image…</M.Text>}
      {!!uploadError && (
        <M.Text role="alert" color="red">
          {uploadError instanceof Error
            ? uploadError.message
            : 'Schedule detection failed. Please try again.'}
        </M.Text>
      )}
      {!!imageLoadingError && !images.length && (
        <M.Text role="alert" color="red">
          Could not load schedule images. Please try again.
        </M.Text>
      )}

      {!!images.length && <ImageGallery images={images} />}

      {!isLoading && !images.length && (
        <M.Text>No schedule images uploaded yet</M.Text>
      )}
    </Container>
  );
};

const Container = styled(M.Flex)`
  flex-direction: column;
  gap: ${(p) => p.theme.spacing.md};
`;

const DropzoneContainer = styled(M.Flex)`
  flex-direction: column;
  gap: ${(p) => p.theme.spacing.md};
  align-items: center;
`;

export default ImageUploader;
